/**
 * TanStack Query の queryKey 規約（05-ui.md §5.3）。
 *
 * 1. 先頭は必ず `'portal'`。ログアウト時に `removeQueries({queryKey:['portal']})` で一掃する。
 * 2. 一覧キーの末尾はフィルタオブジェクトそのもの（フィルタが変われば別キャッシュ）。
 * 3. mutation 成功時は「一番浅い共通キー」を invalidate する。
 */

export const qk = {
  all: ['portal'] as const,

  publicConfig: () => ['portal', 'public-config'] as const,

  org: () => ['portal', 'org'] as const,
  /** 初期設定の進捗（`GET /onboarding`）。ダッシュボードのチェックリスト。 */
  onboarding: () => ['portal', 'onboarding'] as const,
  members: () => ['portal', 'members'] as const,
  branding: () => ['portal', 'branding'] as const,
  customHostnames: () => ['portal', 'custom-hostnames'] as const,
  exports: (filter?: unknown) =>
    filter === undefined ? (['portal', 'exports'] as const) : (['portal', 'exports', filter] as const),

  domains: () => ['portal', 'domains'] as const,
  domain: (id: string) => ['portal', 'domains', id] as const,
  dnsRecords: (id: string) => ['portal', 'domains', id, 'dns-records'] as const,
  catchAll: (id: string) => ['portal', 'domains', id, 'catch-all'] as const,
  spamPolicy: (id: string) => ['portal', 'domains', id, 'spam'] as const,
  spamStats: (id: string) => ['portal', 'domains', id, 'spam-stats'] as const,
  /** DMARC の段階（`GET /domains/:id/dmarc`）。 */
  dmarc: (id: string) => ['portal', 'domains', id, 'dmarc'] as const,
  /** 独自ホスト名の TLS 証明書（`GET /domains/:id/certificate`）。 */
  certificate: (id: string) => ['portal', 'domains', id, 'certificate'] as const,

  mailboxes: (filter?: unknown) =>
    filter === undefined ? (['portal', 'mailboxes'] as const) : (['portal', 'mailboxes', filter] as const),
  mailbox: (id: string) => ['portal', 'mailboxes', id] as const,
  autoresponder: (id: string) => ['portal', 'mailboxes', id, 'autoresponder'] as const,
  /** 差出人 ACL（`GET /mailboxes/:id/senders`）。 */
  mailboxSenders: (id: string) => ['portal', 'mailboxes', id, 'senders'] as const,

  aliases: (filter?: unknown) =>
    filter === undefined ? (['portal', 'aliases'] as const) : (['portal', 'aliases', filter] as const),
  forwarders: (filter?: unknown) =>
    filter === undefined ? (['portal', 'forwarders'] as const) : (['portal', 'forwarders', filter] as const),
  /**
   * プライベートメールアドレス。**エイリアスとは別のキー空間にする**
   * （`qk.aliases()` を invalidate したときに巻き込まれると、使い捨てアドレスを
   * 数十件抱えた一覧が毎回取り直しになる）。
   */
  privateAddresses: (filter: unknown) => ['portal', 'private-addresses', filter] as const,
  /**
   * **invalidate にはこちらを使う。**
   *
   * この規約の「一覧キーの末尾はフィルタオブジェクトそのもの」と
   * 「一番浅い共通キー を invalidate する」は、素直に書くと**噛み合わない**。
   * TanStack Query の部分一致は「渡したキーの各要素が、対象キーの同じ位置の
   * 要素と部分一致するか」で判定するので、`[..., null]`（3 要素）を渡すと
   * **3 要素目がフィルタオブジェクトの一覧には一致しない**
   * （`partialMatchKey` は片方が `null` の時点で false を返す）。
   *
   * つまり `invalidateQueries({ queryKey: [..., 'private-addresses', null] })` は
   * **1 件も無効化せず、しかもエラーにもならない**。画面には
   * 「発行したのに一覧に出ない」だけが見え、原因は一切表に出ない
   * （2026-08-28 に実機で踏んだ。API は 201 を返していて、
   * 再読み込みすると行は在った）。
   *
   * フィルタを持つ一覧を無効化するときは、**フィルタの位置を含めない
   * 2 要素の接頭辞**を渡すこと。
   */
  privateAddressesAll: () => ['portal', 'private-addresses'] as const,

  usage: () => ['portal', 'usage'] as const,
  mailLogs: (filter?: unknown) =>
    filter === undefined ? (['portal', 'logs', 'mail'] as const) : (['portal', 'logs', 'mail', filter] as const),
  authLogs: (filter?: unknown) =>
    filter === undefined ? (['portal', 'logs', 'auth'] as const) : (['portal', 'logs', 'auth', filter] as const),
  /** 1 通ごとの配送記録（`GET /logs/delivery`）。`mailLogs` とは別の情報。 */
  deliveryLogs: (filter?: unknown) =>
    filter === undefined
      ? (['portal', 'logs', 'delivery'] as const)
      : (['portal', 'logs', 'delivery', filter] as const),
  /** 組織の操作履歴（`GET /logs/audit`）。 */
  auditLogs: (filter?: unknown) =>
    filter === undefined ? (['portal', 'logs', 'audit'] as const) : (['portal', 'logs', 'audit', filter] as const),

  /* ------------------------------------------------ Webメールの AI（0046〜0060） */

  aiPolicy: () => ['portal', 'ai', 'policy'] as const,
  aiPresets: () => ['portal', 'ai', 'presets'] as const,
  /** 月次の使用量。**月ごとに別キー**（過去月は変わらないので長く持てる）。 */
  aiUsage: (month: string) => ['portal', 'ai', 'usage', month] as const,

  /* ------------------------------------------- 受信者のプライバシー保護（0053） */

  privacyPolicy: () => ['portal', 'privacy', 'remote-images'] as const,
  imageProxyHosts: (days: number) => ['portal', 'privacy', 'image-proxy-hosts', days] as const,

  securitySummary: () => ['portal', 'security', 'summary'] as const,
  /** 自組織に効いている IP 遮断（`GET /security/ip-blocks`）。 */
  ipBlocks: () => ['portal', 'security', 'ip-blocks'] as const,
  /** 組織の 2 段階認証ポリシーとメンバーの登録状況。 */
  mfaPolicy: () => ['portal', 'security', 'mfa-policy'] as const,
  sendingLimits: () => ['portal', 'security', 'sending-limits'] as const,
  sessions: () => ['portal', 'sessions'] as const,

  subscription: () => ['portal', 'billing', 'subscription'] as const,
  invoices: (filter?: unknown) =>
    filter === undefined
      ? (['portal', 'billing', 'invoices'] as const)
      : (['portal', 'billing', 'invoices', filter] as const),
  /** 請求書 1 通の明細（`GET /invoices/:id`）。PDF が無い環境で唯一の内訳。 */
  invoice: (id: string) => ['portal', 'billing', 'invoices', 'detail', id] as const,
  plans: () => ['portal', 'billing', 'plans'] as const,

  apiTokens: () => ['portal', 'api-tokens'] as const,

  job: (jobId: string) => ['portal', 'jobs', jobId] as const,

  /* ---------------------------------------------------- 配信コンテンツ CMS */

  contents: (filter?: unknown) =>
    filter === undefined ? (['portal', 'content'] as const) : (['portal', 'content', filter] as const),
  content: (id: string) => ['portal', 'content', id] as const,
  contentRevisions: (id: string) => ['portal', 'content', id, 'revisions'] as const,
  /** 本文つきのリビジョン。**キャッシュは短命**にする（他人の保存を拾えないため）。 */
  contentRevision: (id: string, revision: number | 'latest') =>
    ['portal', 'content', id, 'revisions', revision] as const,
  contentTags: () => ['portal', 'content', 'tags'] as const,
  contentPolicy: () => ['portal', 'content-policies'] as const,
  /** 未決の承認依頼（`/bulk/reviews`）。編集画面と同じキーを共有する。 */
  contentReviews: () => ['portal', 'content', 'reviews'] as const,
  contentTemplates: () => ['portal', 'content', 'templates'] as const,
  contentShareLinks: (id: string) => ['portal', 'content', id, 'share-links'] as const,
  snippetDependents: (id: string) => ['portal', 'content', id, 'dependents'] as const,
  contentSchedules: (filter?: unknown) =>
    filter === undefined
      ? (['portal', 'content', 'schedules'] as const)
      : (['portal', 'content', 'schedules', filter] as const),
  contentImports: () => ['portal', 'content', 'imports'] as const,
  /** 版ごとのリンク死活確認。**版を跨いで使い回さない**（結果は版に紐づく）。 */
  contentLinkChecks: (id: string, revision: number) =>
    ['portal', 'content', id, 'revisions', revision, 'link-checks'] as const,
  /** 差分。**保存しない結果**なので短命キャッシュで扱う。 */
  contentDiff: (id: string, a: number, b: number) => ['portal', 'content', id, 'diff', a, b] as const,
  linkPolicy: () => ['portal', 'content', 'link-policy'] as const,

  assets: (filter?: unknown) =>
    filter === undefined ? (['portal', 'assets'] as const) : (['portal', 'assets', filter] as const),
  asset: (assetId: string) => ['portal', 'assets', assetId] as const,
  assetFolders: () => ['portal', 'assets', 'folders'] as const,
  assetUsages: (assetId: string) => ['portal', 'assets', assetId, 'usages'] as const,
  unusedAssets: (days: number) => ['portal', 'assets', 'unused', days] as const,

  campaigns: (filter?: unknown) =>
    filter === undefined ? (['portal', 'campaigns'] as const) : (['portal', 'campaigns', filter] as const),
  campaign: (id: string) => ['portal', 'campaigns', id] as const,
  campaignCounts: () => ['portal', 'campaigns', 'summary'] as const,

  contactLists: () => ['portal', 'lists'] as const,
  contactList: (id: string) => ['portal', 'lists', id] as const,
  contacts: (listId: string, filter?: unknown) =>
    filter === undefined
      ? (['portal', 'lists', listId, 'contacts'] as const)
      : (['portal', 'lists', listId, 'contacts', filter] as const),
  suppressions: (filter?: unknown) =>
    filter === undefined ? (['portal', 'suppressions'] as const) : (['portal', 'suppressions', filter] as const),
  /** 宛先 CSV の取り込み（`contact_imports`）。コンテンツ側の取り込みとは別物。 */
  contactImports: (listId: string) => ['portal', 'lists', listId, 'imports'] as const,

  /* ------------------------------------------------ 送信元ストリーム / 申込フォーム */

  sendingStreams: () => ['portal', 'sending-streams'] as const,
  sendingStream: (id: string) => ['portal', 'sending-streams', id] as const,

  signupForms: () => ['portal', 'signup-forms'] as const,
  signupForm: (id: string) => ['portal', 'signup-forms', id] as const,
  signupFormSubmissions: (id: string, filter?: unknown) =>
    filter === undefined
      ? (['portal', 'signup-forms', id, 'submissions'] as const)
      : (['portal', 'signup-forms', id, 'submissions', filter] as const),

  /* -------------------------------------------------- 通知・障害情報・問い合わせ */

  notifications: (filter?: unknown) =>
    filter === undefined ? (['portal', 'notifications'] as const) : (['portal', 'notifications', filter] as const),
  /** ヘッダのバッジ。一覧とは別キーにして軽く回す。 */
  notificationCount: () => ['portal', 'notifications', 'unread-count'] as const,
  incidents: (filter?: unknown) =>
    filter === undefined ? (['portal', 'incidents'] as const) : (['portal', 'incidents', filter] as const),
  maintenanceWindows: () => ['portal', 'maintenance-windows'] as const,
  supportTickets: (filter?: unknown) =>
    filter === undefined
      ? (['portal', 'support', 'tickets'] as const)
      : (['portal', 'support', 'tickets', filter] as const),
  supportTicket: (id: string) => ['portal', 'support', 'tickets', id] as const,
  privacyRequests: () => ['portal', 'privacy-requests'] as const,

  /* -------------------------------------------------------------- 処理の状況 */

  jobs: (filter?: unknown) =>
    filter === undefined ? (['portal', 'jobs', 'list'] as const) : (['portal', 'jobs', 'list', filter] as const),

  /**
   * 法定文書への同意（`GET /portal/consents`）。
   *
   * ログイン後のレイアウトが毎回読む。**`staleTime` を長めに取ること**
   * （版が上がるのは年に数回で、画面遷移のたびに引く意味が無い）。
   */
  consents: () => ['portal', 'consents'] as const,

  /** SLA の掲示値・クレジット履歴・申告できる対象月（`GET /portal/billing/sla`）。 */
  sla: () => ['portal', 'billing', 'sla'] as const,

  /** Webhook の購読できるイベント種別（`GET /portal/webhooks/event-types`）。 */
  webhookEventTypes: () => ['portal', 'webhooks', 'event-types'] as const,

  /**
   * 設定情報シート（`GET /portal/mailboxes/setup-sheets?ids=...`）。
   * 印刷画面が 1 回読むだけなので、選んだ ID の集合をそのまま鍵にする。
   */
  setupSheets: (ids: readonly string[]) => ['portal', 'setup-sheets', [...ids].join(',')] as const,

  webhooks: () => ['portal', 'webhooks'] as const,
  /** 未送信のまま残っている Webhook（`GET /webhooks/replay/pending`）。 */
  webhookReplayPending: () => ['portal', 'webhooks', 'replay-pending'] as const,
  webhookDeliveries: (id: string, filter?: unknown) =>
    filter === undefined
      ? (['portal', 'webhooks', id, 'deliveries'] as const)
      : (['portal', 'webhooks', id, 'deliveries', filter] as const),
} as const;
